import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Tooltip } from '@shopify/polaris';
import { AlertTriangleIcon, CaretDownIcon, CaretUpIcon, CheckIcon, PlusIcon, SearchIcon, XIcon } from '@shopify/polaris-icons';
import TOOLS, { GROUPS } from '../tools';
import { useAuth } from '../AuthComponent';
import { Ic, StatusPill } from '../ui';
import { usersApi } from './apiClient';
import { MultiSelect } from '../InventoryRebalancing/Selects';
import { Modal } from '../ItAssets/common';

const ADMIN = 'admin';
const FILTERS = [['all', 'All users'], ['admin', 'Admins'], ['setup', 'Needs setup'], ['off', 'Deactivated']];
// Everything that can be granted, Settings included.
const APPS = TOOLS.filter((t) => !t.noPermission);
const APP_IDS = APPS.map((t) => t.id);
const isAdmin = (u) => u.permissions.includes(ADMIN);
const ACTIVITY_SINCE = 'Sep 29, 2026';       // sign-ins were not recorded before this
// An admin has every app, so they match any app filter.
const has = (u, app) => isAdmin(u) || u.permissions.includes(app);
const needsSetup = (u) => u.is_active && (!u.email_verified || u.must_reset_password);

// One sortable value per column. `text` compares with localeCompare so accented
// names sort beside their plain spelling; everything else is a number. A null is
// "no value" and always sorts last, whichever direction the column is in —
// an account that has never signed in is not the earliest sign-in.
const SORTS = {
  name: { label: 'Name', text: true, get: (u) => u.full_name },
  username: { label: 'Username', text: true, get: (u) => u.username },
  // Admins have every app including ones added later, so they outrank any count.
  access: { label: 'Access', get: (u) => (isAdmin(u) ? Number.MAX_SAFE_INTEGER : u.permissions.filter((p) => APP_IDS.includes(p)).length) },
  status: { label: 'Status', get: (u) => (!u.is_active ? 2 : needsSetup(u) ? 0 : 1) },   // the accounts needing work first
  last_sign_in: { label: 'Last sign-in', get: (u) => (u.last_sign_in ? Date.parse(u.last_sign_in) : null) },
};

function sortUsers(rows, key, dir) {
  const { get, text } = SORTS[key];
  const d = dir === 'asc' ? 1 : -1;
  const blank = (v) => v === null || v === undefined || v === '';
  return [...rows].sort((a, b) => {
    const x = get(a);
    const y = get(b);
    if (blank(x) || blank(y)) return blank(x) && blank(y) ? 0 : blank(x) ? 1 : -1;
    const c = text ? String(x).localeCompare(String(y)) : Math.sign(x - y);
    return (c * d) || a.full_name.localeCompare(b.full_name);     // ties read alphabetically
  });
}

function SortTh({ col, sort, onSort }) {
  const on = sort.key === col;
  return (
    <th className="srt" aria-sort={on ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
      <button type="button" className={`uh-th-sort${on ? ' on' : ''}`} onClick={() => onSort(col)}>
        {SORTS[col].label}
        <Ic source={on && sort.dir === 'desc' ? CaretDownIcon : CaretUpIcon} sm />
      </button>
    </th>
  );
}

function Alert({ tone = '', children }) {
  return <div className={`uh-alert ${tone}`}><Ic source={tone === 'ok' ? CheckIcon : AlertTriangleIcon} sm /><div>{children}</div></div>;
}

function UserStatus({ user }) {
  if (!user.is_active) return <StatusPill tone="can">Deactivated</StatusPill>;
  if (needsSetup(user)) return <StatusPill tone="hold">{user.email_verified ? 'Must reset password' : 'Needs setup'}</StatusPill>;
  return <StatusPill tone="ful">Active</StatusPill>;
}

function Access({ user }) {
  if (isAdmin(user)) return <span className="uh-us-acc"><span className="uh-us-admin">Admin</span><span className="uh-muted">All apps</span></span>;
  const n = user.permissions.filter((p) => APP_IDS.includes(p)).length;
  if (!n) return <span className="uh-faint">No apps</span>;
  // One bar per app, lit when granted. A lit bar names its app on hover; an
  // unlit one is left bare, so hovering the row doesn't fire off tooltips for
  // the 15 apps the person hasn't got. No title on the wrapper either: it would
  // surface a second, native tooltip over the Polaris one.
  return (
    <span className="uh-us-acc">
      <span className="uh-us-dots">
        {APPS.map((t) => (user.permissions.includes(t.id) ? (
          <Tooltip key={t.id} content={t.title} preferredPosition="above"><i /></Tooltip>
        ) : (
          <i key={t.id} className="off" />
        )))}
      </span>
      <span>{n} of {APP_IDS.length}</span>
    </span>
  );
}

// Apps grouped like the sidebar, as toggle chips. An admin has every app, so the chips lock on.
function AppChips({ permissions, admin, onChange }) {
  const toggle = (id) => onChange(permissions.includes(id) ? permissions.filter((p) => p !== id) : [...permissions, id]);
  return (
    <div>
      {GROUPS.map((g) => {
        const tools = APPS.filter((t) => t.group === g.id);
        if (!tools.length) return null;
        return (
          <div key={g.id} className="uh-us-grp">
            <div className="gl">{g.title}</div>
            <div className="chips">
              {tools.map((t) => (
                <label key={t.id} className="uh-us-ck">
                  <input type="checkbox" checked={admin || permissions.includes(t.id)} disabled={admin} onChange={() => toggle(t.id)} />
                  {t.navTitle}
                </label>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// Change access for everyone ticked: each tool is Leave, Add or Remove. Admins already have every tool and are skipped.
function BulkAccess({ people, onClose, onDone }) {
  const [plan, setPlan] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const regular = people.filter((u) => !isAdmin(u));
  const admins = people.filter(isAdmin);
  const title = (id) => { const t = APPS.find((x) => x.id === id); return t.title || t.navTitle; };
  const setOne = (id, v) => setPlan((p) => { const n = { ...p }; if (v === 'keep') delete n[id]; else n[id] = v; return n; });
  const lines = Object.entries(plan).map(([id, v]) => {
    const n = regular.filter((u) => (v === 'add' ? !u.permissions.includes(id) : u.permissions.includes(id))).length;
    return `${v === 'add' ? 'Add' : 'Remove'} ${title(id)} ${v === 'add' ? 'for' : 'from'} ${n} ${n === 1 ? 'person' : 'people'}`
      + (regular.length - n ? ` (${regular.length - n} ${v === 'add' ? 'already have it' : "don't have it"})` : '');
  });
  const apply = async () => {
    setBusy(true); setError('');
    const pick = (v) => Object.keys(plan).filter((id) => plan[id] === v);
    try { const r = await usersApi.bulkAccess(regular.map((u) => u.id), pick('add'), pick('remove')); onDone(r.changed.length); } catch (e) { setError(e.message); setBusy(false); }
  };
  return (
    <Modal title={`Change access for ${people.length} ${people.length === 1 ? 'person' : 'people'}`} wide onClose={onClose}
      footer={<>
        <div className="uh-us-bsum">{lines.length ? lines.map((l) => <div key={l}>{l}</div>) : <span className="uh-muted">Pick Add or Remove on any tool. Everything else stays as it is.</span>}</div>
        <button type="button" className="uh-btn" disabled={busy} onClick={onClose}>Cancel</button>
        <button type="button" className="uh-btn primary" disabled={busy || !lines.length || !regular.length} onClick={apply}>
          Apply to {regular.length} {regular.length === 1 ? 'person' : 'people'}</button>
      </>}>
      {error && <Alert tone="crit">{error}</Alert>}
      <div className="uh-us-who">{people.map((u) => <span key={u.id}>{u.full_name}</span>)}</div>
      {admins.length > 0 && <div className="uh-muted" style={{ margin: '8px 0' }}>{admins.map((u) => u.full_name).join(', ')} {admins.length === 1 ? 'is an admin' : 'are admins'} and already {admins.length === 1 ? 'has' : 'have'} every tool, so nothing changes for them. Admin itself is changed one person at a time.</div>}
      {GROUPS.map((g) => {
        const tools = APPS.filter((t) => t.group === g.id);
        if (!tools.length) return null;
        return (
          <div key={g.id} className="uh-us-bgrp">
            <div className="gl">{g.title}</div>
            {tools.map((t) => {
              const v = plan[t.id] || 'keep';
              return (
                <div key={t.id} className="uh-us-brow">
                  <span>{t.navTitle}</span>
                  <span className="uh-muted uh-num">{regular.filter((u) => u.permissions.includes(t.id)).length} of {regular.length} have it</span>
                  <span className="uh-us-seg" role="group" aria-label={t.navTitle}>
                    {[['keep', 'Leave'], ['add', 'Add'], ['remove', 'Remove']].map(([k, label]) => (
                      <button key={k} type="button" className={k} aria-pressed={v === k} onClick={() => setOne(t.id, k)}>{label}</button>))}
                  </span>
                </div>
              );
            })}
          </div>
        );
      })}
    </Modal>
  );
}

// One card for both jobs: no row selected is a blank New user form; selecting a row edits that person.
function UserPanel({ user, actorIsAdmin, onSaved, onClose }) {
  const creating = !user;
  const [fullName, setFullName] = useState(user?.full_name || '');
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [permissions, setPermissions] = useState(user?.permissions || []);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const admin = permissions.includes(ADMIN);
  const locked = !creating && isAdmin(user) && !actorIsAdmin;     // the server refuses this too
  const appsOnly = permissions.filter((p) => p !== ADMIN);

  const run = async (fn, done) => {
    setBusy(true); setError(''); setNotice('');
    try { await fn(); if (done) setNotice(done); onSaved(); } catch (e) { setError(e.message || String(e)); } finally { setBusy(false); }
  };
  const create = () => run(async () => {
    await usersApi.create({ full_name: fullName, email, username, permissions });
    setNotice(`${fullName} created. A link to verify their email and set a password was sent to ${email}; they can sign in once they've used it.`);
    setFullName(''); setEmail(''); setUsername(''); setPermissions([]);
  });
  const save = () => run(() => usersApi.update(user.id, { full_name: fullName, permissions }), 'Saved.');
  const toggleActive = () => run(() => usersApi.update(user.id, { is_active: !user.is_active }));
  const sendLink = () => run(() => usersApi.resendSetupLink(user.id), 'Link sent.');

  return (
    <div className="uh-card">
      <div className="uh-card-h">
        <h2 className="uh-h2 lg">{creating ? 'New user' : `Edit ${user.full_name}`}</h2>
        <span className="uh-sp" />
        {!creating && <UserStatus user={user} />}
        <button type="button" className="uh-btn slim uh-us-x" aria-label="Close" onClick={onClose}><Ic source={XIcon} sm /></button>
      </div>
      <div className="uh-card-b uh-us-form">
        <div className="uh-us-frow">
          <div className="uh-fld full"><label htmlFor="us-name">Full name</label>
            <input id="us-name" className="uh-inp" value={fullName} onChange={(e) => setFullName(e.target.value)} autoComplete="off" disabled={locked} /></div>
          <div className="uh-fld"><label htmlFor="us-email">Email</label>
            <input id="us-email" className="uh-inp" type="email" value={creating ? email : user.email} onChange={(e) => setEmail(e.target.value)}
              placeholder="jdoe@soccerpost.com" autoComplete="off" disabled={!creating} /></div>
          <div className="uh-fld"><label htmlFor="us-username">Username</label>
            <input id="us-username" className="uh-inp" value={creating ? username : user.username} onChange={(e) => setUsername(e.target.value)}
              autoComplete="off" disabled={!creating} /></div>
          {creating && <div className="uh-help full">Must be a @soccerpost.com address. A link to verify it and set a password is sent there. The username is what they type to sign in.</div>}
        </div>

        <div className="uh-us-sect">
          <h3 className="uh-h2">Access</h3><span className="uh-sp" />
          {!admin && !locked && (
            <>
              <button type="button" className="uh-link" onClick={() => setPermissions([...APP_IDS, ...permissions.filter((p) => p === ADMIN)])}>All</button>
              <button type="button" className="uh-link" onClick={() => setPermissions([])}>None</button>
            </>
          )}
        </div>
        <label className={`uh-us-adm${admin ? ' on' : ''}`}>
          <input type="checkbox" checked={admin} disabled={!actorIsAdmin || locked}
            onChange={(e) => setPermissions(e.target.checked ? [ADMIN] : [])} />
          <span><b>Admin</b>
            <span>{actorIsAdmin ? 'Can open every app, including ones added later, and manage users.' : 'Can open every app and manage users. Only an admin can change this.'}</span></span>
        </label>
        <AppChips permissions={appsOnly} admin={admin} onChange={(v) => !locked && setPermissions(v)} />

        {error && <Alert tone="crit">{error}</Alert>}
        {notice && <Alert tone="ok">{notice}</Alert>}
        {locked && <Alert>This person is an admin. Only an admin can change their account.</Alert>}

        <div className="uh-us-foot">
          {creating ? (
            <button type="button" className="uh-btn primary" disabled={busy || !fullName.trim() || !email.trim() || !username.trim()} onClick={create}>Create user</button>
          ) : (
            <>
              <button type="button" className="uh-btn primary" disabled={busy || locked} onClick={save}>Save changes</button>
              <button type="button" className="uh-btn" disabled={busy} onClick={sendLink}>
                {user.email_verified ? 'Send password reset link' : 'Resend setup link'}
              </button>
              <span className="uh-sp" />
              <button type="button" className="uh-btn uh-us-danger" disabled={busy || locked} onClick={toggleActive}>
                {user.is_active ? 'Deactivate' : 'Reactivate'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const toolLabel = (id) => (id === 'sign-in' ? 'Sign-in' : TOOLS.find((t) => t.id === id)?.navTitle || id);
const timeOf = (iso) => new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
function dayOf(iso) {
  const d = new Date(iso);
  const today = new Date();
  const days = Math.round((new Date(today.getFullYear(), today.getMonth(), today.getDate()) - new Date(d.getFullYear(), d.getMonth(), d.getDate())) / 86400000);
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  return d.toLocaleDateString([], { month: 'short', day: 'numeric', ...(d.getFullYear() !== today.getFullYear() ? { year: 'numeric' } : {}) });
}

// Sign-ins and what a person did in each tool, newest first. Admins only; the server enforces it too.
function ActivityCard({ user }) {
  const [items, setItems] = useState([]);
  const [tools, setTools] = useState([]);
  const [lastSignIn, setLastSignIn] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [tool, setTool] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async (before) => {
    setLoading(true); setError('');
    try {
      const r = await usersApi.activity(user.id, { tool, before });
      setItems((prev) => (before ? [...prev, ...r.items] : r.items));
      setHasMore(r.has_more); setTools(r.tools); setLastSignIn(r.last_sign_in);
    } catch (e) { setError(e.message || String(e)); } finally { setLoading(false); }
  }, [user.id, tool]);
  useEffect(() => { load(null); }, [load]);

  const days = [];
  items.forEach((it) => {
    const label = dayOf(it.at);
    if (!days.length || days[days.length - 1].label !== label) days.push({ label, rows: [] });
    days[days.length - 1].rows.push(it);
  });

  return (
    <div className="uh-card">
      <div className="uh-card-h">
        <h2 className="uh-h2 lg">Activity</h2><span className="uh-sp" />
        <span className="uh-help">{lastSignIn ? <>Last sign-in <b>{dayOf(lastSignIn).toLowerCase()} {timeOf(lastSignIn)}</b></> : 'No sign-in recorded'}</span>
      </div>
      {tools.length > 1 && (
        <div className="uh-us-actbar">
          {['', ...tools].map((t) => (
            <button key={t || 'all'} type="button" className="uh-us-pill" aria-pressed={tool === t} onClick={() => setTool(t)}>{t ? toolLabel(t) : 'All'}</button>
          ))}
        </div>
      )}
      <div className="uh-card-b">
        {error && <Alert tone="crit">{error}</Alert>}
        {!error && !loading && items.length === 0 && <span className="uh-muted">No activity recorded yet.</span>}
        {days.map((d) => (
          <React.Fragment key={d.label}>
            <div className="uh-us-dayh">{d.label}</div>
            <ul className="uh-tl">
              {d.rows.map((it) => (
                <li key={it.id} className={it.tone === 'ok' ? 'ok' : it.tone === 'bad' ? 'bad' : ''}>
                  <span className="pt" />
                  <div>
                    <div>{it.text}</div>
                    <div className="uh-us-meta"><span className="uh-us-tool">{toolLabel(it.tool)}</span><span>{timeOf(it.at)}</span></div>
                  </div>
                </li>
              ))}
            </ul>
          </React.Fragment>
        ))}
        {loading && <span className="uh-muted">Loading…</span>}
      </div>
      <div className="uh-tfoot">
        <span>Kept for 12 months. Sign-in history starts {ACTIVITY_SINCE}.</span><span className="uh-sp" />
        {hasMore && <button type="button" className="uh-btn slim" disabled={loading} onClick={() => load(items[items.length - 1].id)}>Load more</button>}
        <span>Only admins can see this</span>
      </div>
    </div>
  );
}

function Users() {
  const { permissions: mine } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedId, setSelectedId] = useState(null);       // null = the New user form, when the panel is open
  const [panelOpen, setPanelOpen] = useState(false);         // closed until a row or New user is clicked
  const [apps, setApps] = useState([]);                      // apps to filter by; empty = no app filter
  const [appMode, setAppMode] = useState('any');             // any | all of the chosen apps
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [sort, setSort] = useState({ key: 'name', dir: 'asc' });
  const [ticked, setTicked] = useState(() => new Set());      // user ids chosen for Change access
  const [bulkOpen, setBulkOpen] = useState(false);
  const [notice, setNotice] = useState('');
  // A new column starts ascending; clicking the one already sorted reverses it.
  const onSort = (key) => setSort((s) => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }));

  const load = async () => {
    setError('');
    try { setUsers(await usersApi.list()); } catch (e) { setError(e.message || String(e)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    const matched = users.filter((u) => (!q || `${u.full_name} ${u.email} ${u.username}`.toLowerCase().includes(q))
      && (filter === 'all' || (filter === 'admin' && isAdmin(u)) || (filter === 'setup' && needsSetup(u)) || (filter === 'off' && !u.is_active))
      && (!apps.length || (appMode === 'all' ? apps.every((a) => has(u, a)) : apps.some((a) => has(u, a)))));
    return sortUsers(matched, sort.key, sort.dir);
  }, [users, search, filter, sort, apps, appMode]);
  const allTicked = shown.length > 0 && shown.every((u) => ticked.has(u.id));
  const tick = (id) => setTicked((p) => { const n = new Set(p); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const tickShown = () => setTicked((p) => { const n = new Set(p); shown.forEach((u) => (allTicked ? n.delete(u.id) : n.add(u.id))); return n; });
  const bulkDone = (n) => { setBulkOpen(false); setTicked(new Set()); setNotice(`Changed access for ${n} ${n === 1 ? 'person' : 'people'}. Each change is in their Activity.`); load(); };
  const openNew = () => { setSelectedId(null); setPanelOpen(true); };
  const openUser = (id) => { if (panelOpen && id === selectedId) setPanelOpen(false); else { setSelectedId(id); setPanelOpen(true); } };
  const selected = users.find((u) => u.id === selectedId) || null;
  const stat = (v, k) => <div className="uh-stat"><span className="v">{v}</span><span className="k">{k}</span></div>;

  return (
    <div className="uh uh-page">
      <div className="uh-ph">
        <div>
          <h1 className="uh-h1">Users</h1>
          <div className="uh-sub">Who can sign in to Utility Hub, and which apps they can open.</div>
        </div>
        <div className="uh-statcard">
          <div className="stats">
            {stat(users.length, 'Users')}
            {stat(users.filter((u) => u.is_active && !needsSetup(u)).length, 'Active')}
            {stat(users.filter(isAdmin).length, 'Admins')}
            {stat(users.filter(needsSetup).length, 'Needs setup')}
          </div>
        </div>
      </div>

      {error && <Alert tone="crit">{error}</Alert>}

      <div className={`uh-us-grid${panelOpen ? ' open' : ''}`}>
        <div className="uh-card uh-us-table">
          <div className="uh-us-tbar">
            <div className="uh-search">
              <Ic source={SearchIcon} sm />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, email or username" aria-label="Search users" />
            </div>
            <select className="uh-select" aria-label="Filter users" value={filter} onChange={(e) => setFilter(e.target.value)}>
              {FILTERS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
            <div className="uh-us-apps">
              <MultiSelect id="us-apps" noun="apps" options={APPS.map((t) => ({ value: t.id, label: t.navTitle }))} value={apps} onChange={setApps} allLabel="All apps" />
            </div>
            {apps.length > 1 && (
              <select className="uh-select" aria-label="Match any or all of the chosen apps" value={appMode} onChange={(e) => setAppMode(e.target.value)}>
                <option value="any">Any of these</option><option value="all">All of these</option>
              </select>
            )}
            <span className="uh-sp" />
            <button type="button" className="uh-btn primary" onClick={openNew}><Ic source={PlusIcon} sm />New user</button>
          </div>
          <div className="uh-tscroll">
            <table className="uh-tbl">
              <thead>
                <tr>
                  <th style={{ width: 34 }}><input type="checkbox" checked={allTicked} onChange={tickShown} aria-label="Select everyone shown" /></th>
                  {['name', 'username', 'access', 'status', 'last_sign_in'].map((col) => (
                    <SortTh key={col} col={col} sort={sort} onSort={onSort} />
                  ))}
                  <th />
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={7} className="uh-empty">Loading users…</td></tr>}
                {!loading && shown.length === 0 && <tr><td colSpan={7} className="uh-empty">{users.length ? 'No users match' : 'No users yet. Click New user to create one.'}</td></tr>}
                {shown.map((u) => (
                  <tr key={u.id} className={`click${panelOpen && u.id === selectedId ? ' sel' : ''}`} onClick={() => openUser(u.id)}>
                    <td onClick={(e) => e.stopPropagation()}><input type="checkbox" checked={ticked.has(u.id)} onChange={() => tick(u.id)} aria-label={`Select ${u.full_name}`} /></td>
                    <td><div className="nm">{u.full_name}</div><div className="sm">{u.email}</div></td>
                    <td className="uh-mono">{u.username}</td>
                    <td><Access user={u} /></td>
                    <td><UserStatus user={u} /></td>
                    <td className="uh-us-when">
                      {u.last_sign_in ? (
                        <Tooltip content={new Date(u.last_sign_in).toLocaleString()} preferredPosition="above">
                          <span><span>{dayOf(u.last_sign_in)}</span><span className="sm">{timeOf(u.last_sign_in)}</span></span>
                        </Tooltip>
                      ) : <span className="uh-faint">Never</span>}
                    </td>
                    <td className="r">
                      <button type="button" className="uh-btn slim" onClick={(e) => { e.stopPropagation(); openUser(u.id); }}>
                        {panelOpen && u.id === selectedId ? 'Close' : 'Edit'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="uh-tfoot"><span>{shown.length} of {users.length} users</span><span className="uh-sp" />{notice && <span>{notice}</span>}</div>
          {ticked.size > 0 && (
            <div className="uh-us-bulk">
              <b>{ticked.size} selected</b>
              <button type="button" className="uh-btn slim" onClick={() => setTicked(new Set())}>Clear</button>
              <button type="button" className="uh-btn slim primary" onClick={() => setBulkOpen(true)}>Change access…</button>
            </div>
          )}
          {bulkOpen && <BulkAccess people={users.filter((u) => ticked.has(u.id))} onClose={() => setBulkOpen(false)} onDone={bulkDone} />}
        </div>

        <div className="uh-us-panel" aria-hidden={!panelOpen}>
          <div className="uh-us-panel-in">
            <UserPanel key={selected ? selected.id : 'new'} user={selected} actorIsAdmin={(mine || []).includes(ADMIN)} onSaved={load} onClose={() => setPanelOpen(false)} />
          </div>
        </div>
        {panelOpen && selected && (mine || []).includes(ADMIN) && (
          <div className="uh-us-activity"><ActivityCard key={selected.id} user={selected} /></div>
        )}
      </div>
    </div>
  );
}

export default Users;
